// eslint-disable-next-line unused-imports/no-unused-imports, no-unused-vars
import { OrgUser } from '../../../../models/User/OrgUser'; // used for type checking
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { getColorForGroup } from '../../../../utils/colorUtils';
import {
  ExpandLess,
  ExpandMore,
  SettingsOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import GroupConfigurationModal from '../../../../components/modals/group_configuration/GroupConfigurationModal';
import { useUserUpdaterProvider } from '../../../../context_providers/user_context/UserUpdaterProvider';
import { isAdmin } from '../../../../components/routing/UserRoutes/AcceptedRoles';
import { useGroupUserStatuses } from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import LocationSharingPanelUser from './LocationSharingPanelUser';
import { useEmergencyData } from '@/stores/map/emergencyStore';
import ConnectionStatus from '@/enums/ConnectionStatus';
import COLORS from '@/resources/style/Themes';
import Group from '@/models/Organization/Group';

/**
 * @typedef {{
 * group: Group,
 * theme: Object,
 * isExpanded: boolean,
 * setExpandedGroups: Function,
 * users: Array<OrgUser>,
 * isVisible: boolean,
 * handleToggleGroup: Function,
 * searchTerm: string
 * }} LocationSharingGroupItemProps
 *
 */

/**
 *
 * @param {LocationSharingGroupItemProps} param0
 * @returns
 */
export default function LocationSharingGroupItem({
  group,
  theme,
  isExpanded,
  setExpandedGroups,
  users,
  isVisible,
  handleToggleGroup,
  searchTerm,
}) {
  const { userRole } = useUserUpdaterProvider();
  const emergencyData = useEmergencyData();
  const [modalOpen, setModalOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const activeEmergencies = useMemo(() => {
    return Object.values(emergencyData || {}).filter((element) =>
      element.emergency.isActiveEmergency(),
    );
  }, [emergencyData]);

  const isCurrentUserAdmin = useMemo(() => isAdmin(userRole), [userRole]);

  const userStatuses = useGroupUserStatuses(group.group_name);

  const filteredAndSortedUsersWithStatus = useMemo(() => {
    var filtered = !searchTerm
      ? users
      : users.filter((user) => {
          const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
          return (
            fullName.includes(searchTerm.toLowerCase()) ||
            user.phone_number.includes(searchTerm)
          );
        });
    filtered = filtered.map((u) => {
      return {
        user: u,
        userStatus: userStatuses?.user_statuses?.[u.auth_id] || null,
      };
    });

    // 0 = emergency
    // 1 = disconnected
    // 2 = active + marker
    // 3 = active + no marker
    // 4 = inactive (everything else)
    const rank = (i) => {
      if (
        activeEmergencies.some(
          (emergency) =>
            emergency?.emergency?.creator_auth_id === i.user.auth_id,
        )
      ) {
        return 0; // Emergency users are always ranked first
      }
      if (!i.userStatus) {
        return 4; // treat null/undefined as inactive
      }
      const status = i.userStatus.getCalculatedConnectionStatus();

      switch (status) {
        case ConnectionStatus.Unknown:
          return 1; // Disconnected users are ranked second
        case ConnectionStatus.Active:
        case ConnectionStatus.CheckingConnection:
          if (i.userStatus.isSharingLocation()) {
            return 2;
          } else {
            return 3;
          }
        default:
          return 4;
      }
    };

    return [...filtered].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;

      // tiebreaker: name (case-insensitive, null-safe)
      return (a.user.first_name || '').localeCompare(
        b.user.first_name || '',
        undefined,
        {
          sensitivity: 'base',
        },
      );
    });
  }, [users, searchTerm, userStatuses]);

  return (
    <>
      <ListItem
        sx={{
          px: 2,
          py: 0.5,
          backgroundColor: isExpanded ? theme.expandedGroupBg : 'transparent',
          cursor: 'pointer',
        }}
        onClick={() =>
          setExpandedGroups((prev) => ({
            ...prev,
            [group.group_name]: !prev[group.group_name],
          }))
        }
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <Tooltip
          title={
            isVisible
              ? t(StringKeys.CLICK_TO_HIDE)
              : t(StringKeys.CLICK_TO_SHOW)
          }
          placement="right-start"
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -8],
                  },
                },
              ],
            },
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleToggleGroup(group.group_name);
              setIsHovering(false);
            }}
            size="small"
            sx={{
              p: 1,
              color: isVisible
                ? getColorForGroup(group.group_name)
                : theme.disabledIcon,
              opacity: !isVisible || isHovering ? 1 : 0,
              pointerEvents: 'auto',
            }}
          >
            {isVisible ? (
              <Visibility sx={{ fontSize: 20 }} />
            ) : (
              <VisibilityOff sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
        <ListItemText
          primary={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isVisible
                    ? getColorForGroup(group.group_name)
                    : theme.mutedText,
                  fontWeight: isVisible ? 'medium' : 'normal',
                }}
              >
                {group.group_name}
              </Typography>
            </Box>
          }
          sx={{ my: 0 }}
        />
        {isCurrentUserAdmin && (
          <Tooltip
            title={t(StringKeys.CONFIGURE_GROUP_LOCATION_SETTINGS)}
            arrow
          >
            <SettingsOutlined
              sx={{
                mr: 1,
                fontSize: 'large',
                color: COLORS.SAFEGUARD_BLUE,
                cursor: 'pointer',
                '&:hover': {
                  color: COLORS.SAFEGUARD_BLUE,
                  transform: 'scale(1.2)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(true);
              }}
            />
          </Tooltip>
        )}

        {isExpanded ? (
          <ExpandLess sx={{ color: theme.mutedText }} />
        ) : (
          <ExpandMore sx={{ color: theme.mutedText }} />
        )}
      </ListItem>

      <Collapse
        in={isExpanded}
        timeout="auto"
        unmountOnExit
        onMouseEnter={() => setIsHovering(false)}
      >
        <List component="div" disablePadding>
          {filteredAndSortedUsersWithStatus.map((i) => {
            return (
              <LocationSharingPanelUser
                key={i.user.auth_id}
                theme={theme}
                user={i.user}
                userStatus={i.userStatus}
                group={group}
              />
            );
          })}
          {users.length === 0 && (
            <ListItem sx={{ pl: 5, py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.mutedText,
                  fontStyle: 'italic',
                }}
              >
                {t(StringKeys.NO_USERS_IN_THIS_GROUP)}
              </Typography>
            </ListItem>
          )}
        </List>
      </Collapse>
      <Divider sx={{ borderColor: theme.divider }} />
      <GroupConfigurationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        group={group}
      />
    </>
  );
}
